import { useEffect } from 'react';
import { AxiosError } from 'axios';
import { purgeLegacyAuthStorage, useAuthStore } from '@/stores/auth';
import { isRefreshRejected, problemRetryAfterSeconds, renewSessionFromCookie } from './api';
import { isExplicitLogout } from './explicit-logout';

/**
 * Restaura a sessão ao carregar a página (C8 da change platform-backoffice-hardening). O access
 * token só existe em memória; quem recarrega a página volta a ter um trocando o cookie
 * `platform_refresh` (`HttpOnly`) num `POST /platform/auth/refresh` sem corpo.
 *
 * - 200: grava o access em memória, e a sessão fica `authenticated`.
 * - 401: sem cookie ou cookie recusado; a sessão fica `anonymous`, e a rota protegida manda ao
 *   login uma vez. O 400 e o 415 da API anterior ao cookie, que exige o refresh no corpo, contam
 *   como 401: sem o cookie, ela não tem sessão a restaurar (`isRefreshRejected`).
 * - 429, 5xx ou rede: a sessão fica `unavailable`, com a mensagem e o botão de nova tentativa.
 *   Nada é limpo, ninguém é mandado ao login e não há nova chamada automática: o `/refresh` tem
 *   limite por IP (60 por minuto, contador próprio, separado do login), e um laço o queimaria.
 *
 * - Depois de um "Sair" explícito nesta aba (`lib/explicit-logout.ts`): nenhuma chamada. A sessão
 *   fica `anonymous` até o próximo login, mesmo que o `/logout` tenha falhado e o cookie siga vivo.
 *
 * Uma só renovação por carregamento: a promessa fica guardada, e a montagem dupla do
 * `StrictMode` (ou qualquer segunda chamada) recebe a mesma. Só {@link retrySessionBootstrap},
 * depois de `unavailable`, faz outra.
 */
let bootstrapPromise: Promise<void> | null = null;

export function bootstrapSession(): Promise<void> {
  if (!bootstrapPromise) {
    bootstrapPromise = restoreSession();
  }
  return bootstrapPromise;
}

/** Nova tentativa manual, só a partir de `unavailable`; nos demais estados, não chama a API. */
export function retrySessionBootstrap(): Promise<void> {
  if (useAuthStore.getState().sessionStatus !== 'unavailable') {
    return bootstrapPromise ?? Promise.resolve();
  }
  bootstrapPromise = null;
  useAuthStore.getState().setSessionStatus('restoring');
  return bootstrapSession();
}

/** Dispara {@link bootstrapSession} uma vez, na montagem do `App`. */
export function useSessionBootstrap(): void {
  useEffect(() => {
    void bootstrapSession();
  }, []);
}

/** Só para os testes: esquece a renovação do carregamento anterior. */
export function resetSessionBootstrapForTests(): void {
  bootstrapPromise = null;
}

/** Mensagem do estado `unavailable`, sem detalhe técnico nem token. */
export function describeSessionUnavailable(err: unknown): string {
  if (err instanceof AxiosError && err.response?.status === 429) {
    const seconds = problemRetryAfterSeconds(err);
    return seconds
      ? `Muitas tentativas de entrar. Aguarde ${seconds} s e tente de novo.`
      : 'Muitas tentativas de entrar. Aguarde um pouco e tente de novo.';
  }
  if (err instanceof AxiosError && err.response) {
    return 'O servidor não respondeu como esperado ao restaurar a sessão. Tente de novo em instantes.';
  }
  return 'Sem conexão com o servidor para restaurar a sessão. Confira a rede e tente de novo.';
}

async function restoreSession(): Promise<void> {
  purgeLegacyAuthStorage();
  if (isExplicitLogout()) {
    useAuthStore.getState().setSessionStatus('anonymous');
    return;
  }
  try {
    // `renewSessionFromCookie` grava o access e marca a sessão como `authenticated`.
    await renewSessionFromCookie();
  } catch (err) {
    // Um login concluído enquanto a renovação ia (as telas de MFA gravam o access) vence: a
    // resposta atrasada do carregamento não derruba a sessão nova.
    const { accessToken, setSessionStatus } = useAuthStore.getState();
    if (accessToken) return;
    if (isRefreshRejected(err)) {
      setSessionStatus('anonymous');
      return;
    }
    setSessionStatus('unavailable', describeSessionUnavailable(err));
  }
}
